import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Star, Shield, Search, Calendar, Trash2, Trophy, GraduationCap, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getAvatarUrl } from '../utils/avatarUrl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const levelNames = ['Principiante', 'Base', 'Intermedio', 'Avanzato', 'Esperto'];

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [showMonthly, setShowMonthly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [search, setSearch] = useState('');
  const [updatingUser, setUpdatingUser] = useState(null);

  useEffect(() => {
    checkAdminAndFetch();
  }, []);

  const checkAdminAndFetch = async () => {
    try {
      const adminCheck = await axios.get(`${API_URL}/api/admin/check`, {
        withCredentials: true
      });
      
      if (!adminCheck.data.is_admin) {
        toast.error('Accesso non autorizzato');
        navigate('/dashboard');
        return;
      }
      
      setIsAdmin(true);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Accesso non autorizzato');
      navigate('/dashboard');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [res, monthlyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/stats/monthly`, { withCredentials: true })
      ]);
      setStats(res.data);
      setMonthlyStats(monthlyRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateUserLevel = async (userId, newLevel) => {
    setUpdatingUser(userId);
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/level`,
        { level: newLevel },
        { withCredentials: true }
      );
      
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, level: newLevel } : u
      ));
      
      toast.success(`Livello aggiornato a ${levelNames[newLevel - 1]}`);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento del livello');
    } finally {
      setUpdatingUser(null);
    }
  };

  const deleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        withCredentials: true
      });
      setUsers(users.filter(u => u.user_id !== userId));
      toast.success(`${userName} eliminato dal circolo`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const toggleAdmin = async (userId, userName, currentIsAdmin) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/admin`,
        { is_admin: !currentIsAdmin },
        { withCredentials: true }
      );
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, is_admin: !currentIsAdmin } : u
      ));
      toast.success(
        !currentIsAdmin
          ? `${userName} è ora Maestro!`
          : `${userName} non è più Maestro`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const updateUserGender = async (userId, gender) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/gender`,
        { gender },
        { withCredentials: true }
      );
      setUsers(users.map(u =>
        u.user_id === userId ? { ...u, gender } : u
      ));
      toast.success(`Sesso aggiornato a ${gender === 'M' ? 'Uomo' : 'Donna'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const updateUserFascia = async (userId, fascia) => {
    setUpdatingUser(userId);
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/fascia`, { fascia }, { withCredentials: true });
      const fasciaNames = {1:'Esperto', 2:'Avanzato', 3:'Intermedio', 4:'Base', 5:'Principiante'};
      setUsers(users.map(u => u.user_id === userId ? { ...u, fascia, ranking_points: {1:2000,2:1500,3:1000,4:500,5:100}[fascia] } : u));
      toast.success(`Fascia: ${fasciaNames[fascia]}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setUpdatingUser(null);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(searchLower) ||
      u.surname?.toLowerCase().includes(searchLower) ||
      u.email?.toLowerCase().includes(searchLower)
    );
  });

  if (!isAdmin || loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-slate-400">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" />
            Gestione Giocatori
          </h1>
          <p className="text-slate-500">Modifica i livelli dei giocatori del circolo</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="space-y-4" data-testid="admin-stats">
          {/* Users Stats */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Iscritti al Circolo</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900" data-testid="stat-total-users">{stats.users.total}</p>
                <p className="text-xs text-slate-500 mt-0.5">Totale</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-sky-500" data-testid="stat-male-users">{stats.users.male}</p>
                <p className="text-xs text-slate-500 mt-0.5">Uomini</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-pink-500" data-testid="stat-female-users">{stats.users.female}</p>
                <p className="text-xs text-slate-500 mt-0.5">Donne</p>
              </div>
            </div>
          </div>

          {/* Lessons Stats */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Lezioni</h3>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-600" data-testid="stat-lessons-pending">{stats.lessons.pending}</p>
                <p className="text-xs text-amber-600/70">In attesa</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600" data-testid="stat-lessons-upcoming">{stats.lessons.upcoming}</p>
                <p className="text-xs text-emerald-600/70">Programmate</p>
              </div>
              <div className="bg-sky-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-sky-600" data-testid="stat-lessons-completed">{stats.lessons.completed}</p>
                <p className="text-xs text-sky-600/70">Effettuate</p>
              </div>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <span className="text-sm text-slate-500">Incasso confermato</span>
              <span className="text-lg font-bold text-slate-900" data-testid="stat-revenue">{stats.revenue.total}€</span>
            </div>

            {/* Monthly Breakdown Toggle */}
            <button
              onClick={() => setShowMonthly(!showMonthly)}
              data-testid="toggle-monthly-stats"
              className="w-full flex items-center justify-center gap-1.5 mt-3 py-2 text-sm font-medium text-sky-500 hover:text-sky-600 transition-colors"
            >
              {showMonthly ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showMonthly ? 'Nascondi dettaglio mensile' : 'Dettaglio mensile'}
            </button>

            {showMonthly && (
              <div className="mt-2 space-y-2" data-testid="monthly-stats">
                {monthlyStats.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-3">Nessuna lezione confermata</p>
                ) : (
                  monthlyStats.map(m => {
                    const [year, month] = m.month.split('-');
                    const monthNames = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
                    const label = `${monthNames[parseInt(month) - 1]} ${year}`;
                    return (
                      <div key={m.month} data-testid={`month-${m.month}`}
                        className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                        <div>
                          <span className="text-sm font-semibold text-slate-900">{label}</span>
                          <span className="text-xs text-slate-400 ml-2">{m.lessons} {m.lessons === 1 ? 'lezione' : 'lezioni'}</span>
                        </div>
                        <span className="text-sm font-bold text-emerald-600">{m.revenue}€</span>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gestionale Lezioni Button */}
      <Link
        to="/admin/gestionale"
        data-testid="gestionale-link"
        className="block bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-4 hover:from-teal-700 hover:to-emerald-700 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">Gestionale Lezioni</p>
            <p className="text-emerald-100 text-xs">Gestisci tutte le lezioni (app + manuali)</p>
          </div>
          <ChevronDown className="w-5 h-5 text-white/50 -rotate-90" />
        </div>
      </Link>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Cerca giocatore..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 rounded-2xl border border-slate-200 bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 transition-all outline-none"
        />
      </div>

      {/* Admin Actions */}
      <div className="space-y-3">
        <Link
          to="/admin/ranking"
          className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-slate-100 hover:border-amber-200 transition-all"
        >
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <Trophy className="w-6 h-6 text-amber-500" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">Gestione Classifica</p>
            <p className="text-sm text-slate-500">Ordina i giocatori dal primo all'ultimo</p>
          </div>
          <ArrowLeft className="w-5 h-5 text-slate-400 rotate-180" />
        </Link>

        <Link
          to="/admin/matches"
          className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-slate-100 hover:border-sky-200 transition-all"
        >
          <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
            <Calendar className="w-6 h-6 text-sky-500" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">Gestione Partite</p>
            <p className="text-sm text-slate-500">Visualizza ed elimina partite</p>
          </div>
          <ArrowLeft className="w-5 h-5 text-slate-400 rotate-180" />
        </Link>

        <Link
          to="/admin/lessons"
          data-testid="admin-lessons-link"
          className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-slate-100 hover:border-emerald-200 transition-all"
        >
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-emerald-500" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">Gestione Lezioni</p>
            <p className="text-sm text-slate-500">Prenotazioni e disponibilità</p>
          </div>
          <ArrowLeft className="w-5 h-5 text-slate-400 rotate-180" />
        </Link>

        <Link
          to="/admin/analytics"
          data-testid="admin-analytics-link"
          className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-slate-100 hover:border-purple-200 transition-all"
        >
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900">Statistiche Accessi</p>
            <p className="text-sm text-slate-500">Accessi giornalieri e settimanali</p>
          </div>
          <ArrowLeft className="w-5 h-5 text-slate-400 rotate-180" />
        </Link>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {filteredUsers.map((player) => (
          <div
            key={player.user_id}
            className="bg-white rounded-2xl p-4 border border-slate-100"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-sky-400 rounded-xl flex items-center justify-center text-white font-semibold overflow-hidden">
                {getAvatarUrl(player) ? (
                  <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-xl object-cover" />
                ) : (
                  player.name?.charAt(0)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">
                    {player.name} {player.surname || ''}
                  </p>
                  {player.is_admin && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-600">
                      Maestro
                    </span>
                  )}
                  {player.user_id === user?.user_id && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-600">
                      Tu
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500">{player.email}</p>
              </div>
            </div>

            {/* Level Selector */}
            <div className="flex items-end justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700 mb-2">Livello di gioco:</p>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <button
                      key={level}
                      onClick={() => updateUserLevel(player.user_id, level)}
                      disabled={updatingUser === player.user_id}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        player.level === level
                          ? 'bg-sky-400 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      } ${updatingUser === player.user_id ? 'opacity-50' : ''}`}
                    >
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star
                            key={i}
                            className={`w-3 h-3 ${
                              i <= level 
                                ? player.level === level ? 'fill-white text-white' : 'fill-sky-400 text-sky-400'
                                : player.level === level ? 'text-sky-200' : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="hidden sm:inline">{levelNames[level - 1]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Gender Selector */}
              <div className="mt-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Sesso:</p>
                <div className="flex gap-2">
                  {[{ value: 'M', label: 'Uomo' }, { value: 'F', label: 'Donna' }].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => updateUserGender(player.user_id, opt.value)}
                      data-testid={`gender-${opt.value}-${player.user_id}`}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        player.gender === opt.value
                          ? opt.value === 'F' ? 'bg-pink-400 text-white' : 'bg-sky-400 text-white'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fascia Selector */}
              {!player.is_admin && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">Fascia Ranking:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      {v: 5, label: 'Princ.', color: 'bg-slate-400'},
                      {v: 4, label: 'Base', color: 'bg-orange-400'},
                      {v: 3, label: 'Inter.', color: 'bg-emerald-400'},
                      {v: 2, label: 'Avanz.', color: 'bg-sky-400'},
                      {v: 1, label: 'Esper.', color: 'bg-yellow-400'},
                    ].map((f) => (
                      <button
                        key={f.v}
                        onClick={() => updateUserFascia(player.user_id, f.v)}
                        disabled={updatingUser === player.user_id}
                        data-testid={`fascia-${f.v}-${player.user_id}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          player.fascia === f.v
                            ? `${f.color} text-white`
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        } ${updatingUser === player.user_id ? 'opacity-50' : ''}`}
                      >
                        {f.v}. {f.label}
                      </button>
                    ))}
                    {player.fascia > 0 && (
                      <span className="text-xs text-slate-400 self-center ml-1">
                        {player.ranking_points || {1:2000,2:1500,3:1000,4:500,5:100}[player.fascia]} pts
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Admin Toggle & Delete Buttons */}
              {player.user_id !== user?.user_id && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Toggle Maestro */}
                  <button
                    onClick={() => toggleAdmin(player.user_id, player.name, player.is_admin)}
                    data-testid={`toggle-admin-${player.user_id}`}
                    className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                      player.is_admin
                        ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    <Shield className="w-4 h-4 inline mr-1" />
                    {player.is_admin ? 'Rimuovi Maestro' : 'Rendi Maestro'}
                  </button>

                  {/* Delete User */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare {player.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Questo rimuoverà l'utente dal circolo, le sue chat e lo rimuoverà dalle partite. Questa azione non può essere annullata.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteUser(player.user_id, player.name)}
                          className="rounded-full bg-red-500 hover:bg-red-600"
                        >
                          Elimina
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessun giocatore trovato</p>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
